import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, RotateCcw } from "lucide-react";
import { CvDocument, listArchivedDocuments, restoreDocument } from "features/cv-processor/api/CvProcessorApi";
import { listCategories } from "features/cv-processor/api/CvCategoryApi";

export default function ArchivedCvsPage() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<CvDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [restoring, setRestoring] = useState<number | null>(null);
  const [categoryMap, setCategoryMap] = useState<Record<number, string>>({});

  const cvListPath = localStorage.getItem("authUserType") === "admin" ? "/admin/cv-processor" : "/employee/cv-processor";

  useEffect(() => {
    listArchivedDocuments()
      .then(setDocs)
      .catch(() => setError("Failed to load archived CVs."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    listCategories()
      .then((cats) => {
        const map: Record<number, string> = {};
        cats.forEach((c) => { map[c.id] = c.name; });
        setCategoryMap(map);
      })
      .catch(() => {});
  }, []);

  const handleRestore = async (doc: CvDocument) => {
    setRestoring(doc.id);
    try {
      await restoreDocument(doc.id);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    } catch {
      setError("Could not restore document. Please try again.");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <button
            onClick={() => navigate(cvListPath)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3 font-inter"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to My CVs
          </button>
          <h1 className="text-2xl font-bold text-foreground font-inter">Archived CVs</h1>
          <p className="text-sm text-muted-foreground font-inter mt-1">
            Archived CVs are hidden from your CV list and from job-enhancement pickers. Restore one to bring it back.
          </p>
        </div>

        {error && (
          <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-inter">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20">
            <p className="text-muted-foreground text-sm font-inter">Loading…</p>
          </div>
        ) : docs.length === 0 ? (
          <div className="rounded-2xl border border-border bg-surface/30 py-20 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground font-inter text-sm">No archived CVs.</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border overflow-hidden">
            <table className="w-full text-left text-sm font-inter">
              <thead className="bg-surface-muted/80 text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium w-40 hidden md:table-cell">Archived</th>
                  <th className="px-4 py-3 font-medium w-32 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {docs.map((doc) => (
                  <tr key={doc.id} className="bg-surface/30">
                    <td className="px-4 py-3">
                      <p className="text-foreground font-medium">{doc.title}</p>
                      {doc.category_id && categoryMap[doc.category_id] && (
                        <p className="text-xs text-muted-foreground mt-0.5">{categoryMap[doc.category_id]}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                      {doc.archived_at ? new Date(doc.archived_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRestore(doc)}
                        disabled={restoring === doc.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary hover:text-primary-foreground bg-primary/10 hover:bg-primary border border-primary/30 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {restoring === doc.id ? "Restoring…" : "Restore"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
